import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import type { SaveAndResumeSettings } from '../../types/form';

interface SaveResumeSettingsProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_SETTINGS: SaveAndResumeSettings = {
  enabled: false,
  autoSave: true,
  autoSaveInterval: 30,
  expiryDays: 30,
  showSaveButton: true,
  saveButtonText: 'Save Progress',
  resumePromptText: 'Continue where you left off?',
};

export default function SaveResumeSettings({ formId, isOpen, onClose }: SaveResumeSettingsProps) {
  const [settings, setSettings] = useState<SaveAndResumeSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && formId) {
      loadSettings();
    }
  }, [isOpen, formId]);

  async function loadSettings() {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/forms/${formId}`);
      const data = await response.json() as { settings?: { saveAndResume?: SaveAndResumeSettings } };
      if (data.settings?.saveAndResume) {
        setSettings({ ...DEFAULT_SETTINGS, ...data.settings.saveAndResume });
      } else {
        setSettings(DEFAULT_SETTINGS);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const response = await api.put(`/api/forms/${formId}`, {
        settings: { saveAndResume: settings },
      });
      if (!response.ok) {
        throw new Error('Failed to save settings');
      }
      onClose();
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  function updateSettings(updates: Partial<SaveAndResumeSettings>) {
    setSettings((prev) => ({ ...prev, ...updates }));
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Save & Resume</h2>
            <p className="text-sm text-gray-500">Allow users to save progress and continue later</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <svg className="w-8 h-8 animate-spin text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          ) : (
            <div className="space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Enable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-gray-900">Enable Save & Resume</label>
                  <p className="text-sm text-gray-500">
                    Let users save their progress and continue later
                  </p>
                </div>
                <button
                  onClick={() => updateSettings({ enabled: !settings.enabled })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.enabled ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {settings.enabled && (
                <>
                  {/* Auto-save Section */}
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="font-medium text-gray-900">Auto-save</label>
                        <p className="text-sm text-gray-500">
                          Automatically save progress as users fill the form
                        </p>
                      </div>
                      <button
                        onClick={() => updateSettings({ autoSave: !settings.autoSave })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings.autoSave ? 'bg-primary-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            settings.autoSave ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {settings.autoSave && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Auto-save interval (seconds)
                        </label>
                        <input
                          type="number"
                          min={10}
                          max={300}
                          value={settings.autoSaveInterval || 30}
                          onChange={(e) => updateSettings({ autoSaveInterval: parseInt(e.target.value) || 30 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          How often to auto-save (10-300 seconds)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Manual Save Button */}
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="font-medium text-gray-900">Show Save Button</label>
                        <p className="text-sm text-gray-500">
                          Display a manual save button on the form
                        </p>
                      </div>
                      <button
                        onClick={() => updateSettings({ showSaveButton: !settings.showSaveButton })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings.showSaveButton ? 'bg-primary-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            settings.showSaveButton ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {settings.showSaveButton && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Save Button Text
                        </label>
                        <input
                          type="text"
                          value={settings.saveButtonText || 'Save Progress'}
                          onChange={(e) => updateSettings({ saveButtonText: e.target.value })}
                          placeholder="Save Progress"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    )}
                  </div>

                  {/* Expiry Days */}
                  <div className="border-t border-gray-100 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Saved Progress Expiry (days)
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={settings.expiryDays || 30}
                      onChange={(e) => updateSettings({ expiryDays: parseInt(e.target.value) || 30 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      How long to keep saved progress before it expires (1-365 days)
                    </p>
                  </div>

                  {/* Resume Prompt Text */}
                  <div className="border-t border-gray-100 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Resume Prompt Text
                    </label>
                    <input
                      type="text"
                      value={settings.resumePromptText || 'Continue where you left off?'}
                      onChange={(e) => updateSettings({ resumePromptText: e.target.value })}
                      placeholder="Continue where you left off?"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Message shown when a user returns to the form with saved progress
                    </p>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">How it works</p>
                        <ul className="list-disc list-inside space-y-1 text-blue-700">
                          <li>Progress is saved automatically based on IP address</li>
                          <li>If an email field is filled, progress is also linked to that email</li>
                          <li>Users can share a resume link to continue on another device</li>
                          <li>Saved progress is deleted after successful submission</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
